'use client'

import { useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Logo } from '@/components/ui/logo'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { trpc } from '@/lib/trpc'
import { handleSignOut } from '@/lib/auth-actions'
import {
  ArrowLeft,
  Mic2,
  Music,
  Guitar,
  Disc3,
  PartyPopper,
  Building2,
  Rocket,
  TrendingUp,
  Menu,
  X,
  LogOut,
  Settings,
  Shield,
  Plus,
  ChevronsUpDown,
  Loader2,
  LayoutDashboard,
  Mic,
  Wand2,
  Sparkles,
  Drama,
} from 'lucide-react'

const navItems = [
  { href: '/how-it-works', label: 'Overview', icon: Mic2 },
  { href: '/how-it-works/your-first-event', label: 'Your First Event', icon: Rocket },
  { href: '/how-it-works/street-performing', label: 'Street Performing', icon: Guitar },
  { href: '/how-it-works/bars-restaurants', label: 'Bars & Restaurants', icon: Music },
  { href: '/how-it-works/djs', label: 'DJs', icon: Disc3 },
  { href: '/how-it-works/private-events', label: 'Private Events', icon: PartyPopper },
  { href: '/how-it-works/big-shows', label: 'Big Shows & Concerts', icon: Building2 },
  { href: '/how-it-works/comedians', label: 'Comedians', icon: Mic },
  { href: '/how-it-works/magicians', label: 'Magicians', icon: Wand2 },
  { href: '/how-it-works/karaoke', label: 'Karaoke Hosts', icon: Mic2 },
  { href: '/how-it-works/variety-drag', label: 'Variety & Drag', icon: Sparkles },
  { href: '/how-it-works/more-performers', label: 'More Performers', icon: Drama },
  { href: '/how-it-works/growing-your-fanbase', label: 'Growing Your Fanbase', icon: TrendingUp },
  { href: '/how-it-works/venues', label: 'For Venues', icon: Building2 },
]

interface HowItWorksNavProps {
  user: {
    id: string
    name?: string | null
    email?: string | null
    image?: string | null
    role: string
  } | null
}

export function HowItWorksNav({ user }: HowItWorksNavProps) {
  const pathname = usePathname()
  const router = useRouter()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false)

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'

  // Fetch artists for authenticated users
  const { data: artists, isLoading: artistsLoading } = trpc.artist.getMyArtists.useQuery(
    undefined,
    { enabled: !!user }
  )

  return (
    <>
      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-lg">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            {/* Left side: Hamburger (mobile/tablet) + Logo */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-zinc-400 hover:text-white"
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <Link href="/" className="flex items-center">
                <Logo size="sm" className="sm:hidden" />
                <Logo size="md" className="hidden sm:flex" />
              </Link>
              <div className="hidden sm:flex items-center gap-2 text-zinc-500">
                <span>/</span>
                <span className="text-zinc-300 font-medium">How It Works</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {user ? (
                // Authenticated user
                <>
                  {/* Admin link */}
                  {isAdmin && (
                    <Link href="/admin" className="hidden sm:block">
                      <Button variant="ghost" size="sm" className="gap-2 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Shield className="h-4 w-4" />
                        <span className="hidden md:inline">Admin</span>
                      </Button>
                    </Link>
                  )}

                  {/* Artist Switcher */}
                  {artistsLoading ? (
                    <Button variant="ghost" size="sm" className="gap-2" disabled>
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </Button>
                  ) : artists && artists.length > 0 ? (
                    <DropdownMenu open={artistDropdownOpen} onOpenChange={setArtistDropdownOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="gap-2 text-zinc-300 hover:text-white hidden sm:flex">
                          <Music className="h-4 w-4" />
                          <span className="hidden md:inline">My Artists</span>
                          <ChevronsUpDown className="h-3 w-3 text-zinc-500" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-[240px] bg-zinc-900 border-zinc-700" align="end">
                        <DropdownMenuLabel className="text-zinc-400 text-xs font-normal">
                          Your Artist Accounts
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator className="bg-zinc-700" />
                        {artists.map((artist) => (
                          <DropdownMenuItem
                            key={artist.id}
                            onClick={() => {
                              setArtistDropdownOpen(false)
                              router.push(`/${artist.handle}/dashboard`)
                            }}
                            className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                          >
                            <div className="flex items-center gap-3 w-full">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={artist.profileImageUrl || undefined} />
                                <AvatarFallback className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-xs">
                                  {artist.name.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{artist.name}</p>
                                <p className="text-xs text-zinc-500 truncate">@{artist.handle}</p>
                              </div>
                            </div>
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator className="bg-zinc-700" />
                        <DropdownMenuItem
                          onClick={() => {
                            setArtistDropdownOpen(false)
                            router.push('/select')
                          }}
                          className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Create New Artist
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Link href="/select" className="hidden sm:block">
                      <Button variant="ghost" size="sm" className="gap-2 text-zinc-300 hover:text-white">
                        <Plus className="h-4 w-4" />
                        <span className="hidden md:inline">Create Artist</span>
                      </Button>
                    </Link>
                  )}

                  {/* User Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={user.image || undefined} alt={user.name || ''} />
                          <AvatarFallback className="bg-zinc-700 text-white">
                            {user.name?.charAt(0).toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-700" align="end" forceMount>
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium text-white">{user.name}</p>
                          <p className="text-xs text-zinc-400">{user.email}</p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-zinc-700" />
                      {isAdmin && (
                        <DropdownMenuItem asChild>
                          <Link href="/admin" className="cursor-pointer text-red-400 focus:text-red-300 focus:bg-zinc-800">
                            <Shield className="mr-2 h-4 w-4" />
                            Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link href="/select" className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800">
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          Artist Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/settings" className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800">
                          <Settings className="mr-2 h-4 w-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-zinc-700" />
                      <DropdownMenuItem
                        onClick={() => handleSignOut()}
                        className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                // Not authenticated
                <>
                  <Link href="/" className="hidden sm:flex">
                    <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white gap-2">
                      <ArrowLeft className="h-4 w-4" />
                      Back to Home
                    </Button>
                  </Link>
                  <Link href="/signup">
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                    >
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar Navigation */}
      <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 border-r border-zinc-800 bg-zinc-950/50 overflow-y-auto z-40">
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = pathname === item.href
            const Icon = item.icon
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                  isActive
                    ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                )}
              >
                <Icon className={cn('h-4 w-4', isActive ? 'text-violet-400' : '')} />
                {item.label}
              </Link>
            )
          })}
        </nav>
        
        {/* CTA in sidebar */}
        <div className="p-4 border-t border-zinc-800">
          {user ? (
            // Authenticated: show dashboard link
            <div className="p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20">
              <p className="text-sm text-zinc-300 mb-3">
                Ready to run an event?
              </p>
              <Link href="/select">
                <Button
                  size="sm"
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                >
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          ) : (
            // Not authenticated: show signup CTA
            <div className="p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20">
              <p className="text-sm text-zinc-300 mb-3">
                Ready to connect with your audience?
              </p>
              <Link href="/signup">
                <Button
                  size="sm"
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                >
                  Start Free Today
                </Button>
              </Link>
            </div>
          )}
          
          {/* Contact link for questions */}
          <div className="mt-4 text-center">
            <p className="text-xs text-zinc-500 mb-2">Still have questions?</p>
            <Link 
              href="/contact" 
              className="text-sm text-violet-400 hover:text-violet-300 transition-colors"
            >
              Contact Us →
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile Navigation Drawer */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-40 pt-14 sm:pt-16">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
          />
          <nav className="relative w-72 h-full bg-zinc-900 border-r border-zinc-800 overflow-y-auto">
            <div className="p-4 space-y-1">
              {navItems.map((item) => {
                const isActive = pathname === item.href
                const Icon = item.icon
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileNavOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all',
                      isActive
                        ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                    )}
                  >
                    <Icon className={cn('h-4 w-4', isActive ? 'text-violet-400' : '')} />
                    {item.label}
                  </Link>
                )
              })}
            </div>
            <div className="p-4 border-t border-zinc-800">
              {user ? (
                <Link href="/select" onClick={() => setMobileNavOpen(false)}>
                  <Button
                    className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                  >
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link href="/signup" onClick={() => setMobileNavOpen(false)}>
                  <Button
                    className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                  >
                    Start Free Today
                  </Button>
                </Link>
              )}
              
              {/* Contact link for questions */}
              <div className="mt-4 text-center">
                <p className="text-xs text-zinc-500 mb-2">Still have questions?</p>
                <Link 
                  href="/contact" 
                  onClick={() => setMobileNavOpen(false)}
                  className="text-sm text-violet-400 hover:text-violet-300 transition-colors"
                >
                  Contact Us →
                </Link>
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  )
}

